import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type BillRowDto,
  type DeleteBillInput,
  type DRAWER_REFUSALS,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * The server's answer when a bill holding cash is deleted without saying
 * where that cash went (only while the cash drawer is in use).
 */
const CASH_RETURNED_REQUIRED: (typeof DRAWER_REFUSALS)[number] = 'cash_returned_required';

/**
 * The server's answer when the bill is a balance refill or payout that holds
 * cash, while the drawer is in use: voiding the slip would never move the
 * customer's balance, so the delete is refused and the copy points to
 * Customers › Balance refund.
 */
const REFILL_CASH_NOT_DELETABLE: (typeof DRAWER_REFUSALS)[number] = 'refill_cash_not_deletable';

interface DeleteBillDialogProps {
  bill: BillRowDto;
  currencySymbol: string;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * The void request. `cashReturned` rides only once the question was asked and
 * answered, and a "No" rides as `false`: "the cash stayed in the drawer" and
 * "nobody said" are different facts, and the server asks about the second.
 */
export function deleteBillBody(reason: string, cashReturned: boolean | null): DeleteBillInput {
  return cashReturned === null ? { reason } : { reason, cashReturned };
}

/**
 * Delete a bill (SPEC §5.6 — `bills.delete`, audited).
 *
 * The reason is mandatory and goes into the AuditLog with the bill's before and
 * after state (§3). The copy is deliberately explicit that the bill is voided
 * rather than erased: the row keeps its invoice number and its payments and
 * stops counting toward every report, but it remains auditable — which is what
 * makes a till roll trustworthy.
 *
 * Cash on a deleted bill (owner, 2026-10-12): once the cash drawer is in use,
 * deleting a bill that holds cash asks "Was the cash handed back to the
 * customer?". The server knows both facts — whether the bill holds cash and
 * whether the drawer is on — so it is the server that asks
 * (`cash_returned_required`), and this dialog puts the question in words. Yes
 * books the cash out of the open drawer; No leaves it counted there. Neither
 * is preselected: a guess here is a drawer that will not add up at close.
 * A refill or payout holding cash is refused instead (`refill_cash_not_deletable`):
 * no answer would move the customer's balance back.
 */
export function DeleteBillDialog({
  bill,
  currencySymbol,
  onClose,
  onDeleted,
}: DeleteBillDialogProps) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  /** Set once the server has asked where the bill's cash went. */
  const [askingCash, setAskingCash] = useState(false);
  const [cashReturned, setCashReturned] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/bills/${bill.id}/void`, deleteBillBody(reason, cashReturned));
      onDeleted();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === CASH_RETURNED_REQUIRED) {
        // A question, not a failure: nothing was deleted, and the answer
        // goes with the next press.
        setAskingCash(true);
      } else if (caught instanceof ApiError && caught.code === REFILL_CASH_NOT_DELETABLE) {
        setError(t('bills.deleteRefillCash'));
      } else {
        setError(caught instanceof ApiError ? caught.message : 'Could not delete the bill.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
          <div>
            <h2 className="text-lg font-semibold text-content">{t('bills.deleteTitle')}</h2>
            <p className="mt-1 text-sm text-content-secondary">
              {t('bills.deleteConfirm', {
                invoice: bill.invoiceNumber,
                total: formatIqd(fromMillidinars(bill.totalMd), {
                  currencySymbol,
                  locale: i18n.language,
                }),
              })}
            </p>
          </div>
        </div>

        <p className="mt-3 text-xs text-content-muted">{t('bills.deleteVoidNotice')}</p>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">{t('bills.reason')}</span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            maxLength={300}
            placeholder={t('bills.deleteReasonPlaceholder')}
            className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
          />
        </label>

        {askingCash ? (
          <fieldset className="mt-4" data-cash-question>
            <legend className="mb-2 text-sm font-medium text-content">
              {t('bills.deleteCashQuestion')}
            </legend>
            <div className="flex flex-col gap-2 sm:flex-row">
              {([true, false] as const).map((handedBack) => (
                <button
                  key={String(handedBack)}
                  type="button"
                  data-cash-returned={handedBack ? 'yes' : 'no'}
                  aria-pressed={cashReturned === handedBack}
                  onClick={() => setCashReturned(handedBack)}
                  className={`flex-1 rounded-control border px-3 py-2 text-sm transition-colors ${
                    cashReturned === handedBack
                      ? 'border-primary bg-primary/15 font-medium text-content'
                      : 'border-subtle text-content-secondary hover:bg-hover'
                  }`}
                >
                  {t(handedBack ? 'bills.deleteCashYes' : 'bills.deleteCashNo')}
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !reason.trim() || (askingCash && cashReturned === null)}
            className="rounded-full bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger-hover disabled:opacity-60"
          >
            {t('common.delete')}
          </button>
        </div>
      </form>
    </div>
  );
}
